import {
  FC,
  createContext,
  useRef,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react"

import {
  SearchCriteriaAttributeKeys,
  SearchCriteriaAttributes,
} from "Components/SavedSearchAlert/types"
import { getAllowedSearchCriteria } from "Components/SavedSearchAlert/Utils/savedSearchCriteria"
import { useCreateAlert } from "Components/Alert/Hooks/useCreateAlert"
import { useSystemContext } from "System/SystemContext"
import {
  PreviewSavedSearchAttributes,
  useAlertContextPreviewQuery,
} from "__generated__/useAlertContextPreviewQuery.graphql"
import { Environment, fetchQuery, graphql } from "react-relay"
import { useDebouncedValue } from "Utils/Hooks/useDebounce"
import { CustomRange } from "Components/PriceRange/constants"
import { useAlertTracking } from "Components/Alert/Hooks/useAlertTracking"

type Settings = {
  details: string
  email: boolean
  name: string
  push: boolean
}

type PreviewSavedSearch = NonNullable<
  useAlertContextPreviewQuery["response"]["viewer"]
>["previewSavedSearch"]

export type State = {
  settings: Settings
  criteria: SearchCriteriaAttributes
  searchCriteriaID?: string
  currentArtworkID?: string
  preview: PreviewSavedSearch
}

export const DEFAULT_STATE: State = {
  settings: {
    name: "",
    details: "",
    email: true,
    push: false,
  },
  criteria: {},
  preview: null,
}

type Action =
  | { type: "RESET" }
  | { type: "SET_SETTINGS"; payload: Settings }
  | { type: "SET_CRITERIA"; payload: SearchCriteriaAttributes }
  | {
      type: "SET_CRITERIA_ATTRIBUTE"
      payload: {
        key: SearchCriteriaAttributeKeys
        value: string | string[] | number | boolean | null | CustomRange
      }
    }
  | {
      type: "REMOVE_CRITERIA_ATTRIBUTE_VALUE"
      payload: {
        key: SearchCriteriaAttributeKeys
        value: string | string[] | number | boolean | null | CustomRange
      }
    }
  | { type: "SET_SEARCH_CRITERIA_ID"; payload: string }
  | { type: "SET_PREVIEW"; payload: PreviewSavedSearch }

const reducer = (onReset: () => State) => (state: State, action: Action) => {
  switch (action.type) {
    case "RESET":
      return onReset()

    case "SET_SETTINGS":
      return {
        ...state,
        settings: action.payload,
      }

    case "SET_CRITERIA":
      return {
        ...state,
        criteria: action.payload,
      }

    case "SET_CRITERIA_ATTRIBUTE":
      return {
        ...state,
        criteria: {
          ...state.criteria,
          [action.payload.key]: action.payload.value,
        },
      }

    case "REMOVE_CRITERIA_ATTRIBUTE_VALUE":
      let criteriaValue = state.criteria[action.payload.key]

      if (Array.isArray(criteriaValue)) {
        criteriaValue = criteriaValue.filter(
          currentValue => currentValue !== action.payload.value
        )
      } else {
        criteriaValue = null
      }

      return {
        ...state,
        criteria: {
          ...state.criteria,
          [action.payload.key]: criteriaValue,
        },
      }

    case "SET_SEARCH_CRITERIA_ID":
      return {
        ...state,
        searchCriteriaID: action.payload,
      }

    case "SET_PREVIEW":
      return {
        ...state,
        preview: action.payload,
      }

    default:
      return state
  }
}

const AlertContext = createContext<{
  current: "ALERT_DETAILS" | "ALERT_FILTERS" | "ALERT_CONFIRMATION"
  dispatch: React.Dispatch<Action>
  goToFilters(): void
  goToDetails(): void
  onComplete(): void
  state: State
}>({
  current: "ALERT_DETAILS",
  dispatch: () => {},
  goToDetails: () => {},
  goToFilters: () => {},
  onComplete: () => {},
  state: DEFAULT_STATE,
})

interface AlertProviderProps {
  initialCriteria?: SearchCriteriaAttributes
  currentArtworkID?: string
}

export const AlertProvider: FC<AlertProviderProps> = ({
  children,
  initialCriteria,
  currentArtworkID,
}) => {
  const { relayEnvironment } = useSystemContext()

  const initialState = {
    settings: DEFAULT_STATE.settings,
    criteria:
      getAllowedSearchCriteria(initialCriteria as SearchCriteriaAttributes) ??
      DEFAULT_STATE.criteria,
    currentArtworkID,
    preview: null,
  }
  const basis = useRef<State>(initialState)
  const [current, setCurrent] = useState<
    "ALERT_DETAILS" | "ALERT_FILTERS" | "ALERT_CONFIRMATION"
  >("ALERT_DETAILS")

  const { submitMutation } = useCreateAlert()
  const { createdAlert } = useAlertTracking()

  const handleComplete = async () => {
    try {
      const reponse = await submitMutation({
        variables: {
          input: {
            attributes: state.criteria,
            userAlertSettings: state.settings,
          },
        },
      })
      const searchCriteriaID =
        reponse.createSavedSearch?.savedSearchOrErrors.internalID

      if (searchCriteriaID) {
        dispatch({
          type: "SET_SEARCH_CRITERIA_ID",
          payload: searchCriteriaID,
        })

        createdAlert(searchCriteriaID)
      }
      setCurrent("ALERT_CONFIRMATION")
    } catch (error) {
      console.error("Alert/useAlertContext", error)
    }
  }

  const reset = () => {
    setCurrent("ALERT_DETAILS")
    return initialState
  }

  const [state, dispatch] = useReducer(reducer(reset), initialState)

  useEffect(() => {
    basis.current = state
  }, [state])

  // Fetching the display name and labels for alert name input placeholder and the criteria pills, when the criteria changes.

  const { debouncedValue: debouncedCriteria } = useDebouncedValue({
    value: state.criteria,
    delay: 200,
    settings: { leading: true },
  })

  useEffect(() => {
    const fetchPreview = async (criteriaState: SearchCriteriaAttributes) => {
      const data = await fetchQuery<useAlertContextPreviewQuery>(
        relayEnvironment as Environment,
        graphql`
          query useAlertContextPreviewQuery(
            $attributes: PreviewSavedSearchAttributes
          ) {
            viewer {
              previewSavedSearch(attributes: $attributes) {
                displayName
                labels {
                  displayValue
                  field
                  value
                }
              }
            }
          }
        `,
        { attributes: criteriaState as PreviewSavedSearchAttributes }
      )?.toPromise()

      if (!data?.viewer?.previewSavedSearch) return

      dispatch({
        type: "SET_PREVIEW",
        payload: data?.viewer?.previewSavedSearch as PreviewSavedSearch,
      })
    }

    fetchPreview(debouncedCriteria)
  }, [debouncedCriteria, relayEnvironment])

  return (
    <AlertContext.Provider
      value={{
        current,
        dispatch,
        goToDetails: () => {
          setCurrent("ALERT_DETAILS")
        },
        goToFilters: () => {
          setCurrent("ALERT_FILTERS")
        },
        onComplete: handleComplete,
        state,
      }}
    >
      {children}
    </AlertContext.Provider>
  )
}

export const useAlertContext = () => {
  return useContext(AlertContext)
}
